import { useAppContext } from "@context/app";
import { modalActions } from "@context/modals";
import { useIsMobile } from "@shared/hooks/useIsMobile";
import Button from "@shared/components/button/Button";
import {
  BUTTON_COLOR,
  BUTTON_SIZE,
  BUTTON_VARIANT,
} from "@shared/components/button/types";
import ArrowLeftIcon from "@svg/arrow-left";
import PlayIcon from "@svg/title-play-icon";
import { useAccess } from "@templates/course/hooks/useAccess";
import {
  LESSON_STEP_TYPE,
  Source,
  StepInfo,
  StepPreviewCourseItem,
} from "@types";
import { formatTime } from "@utils/player";
import { useMemo } from "react";
import { MODAL_TYPES } from "types/modals";
import { resolveThumbnailSources } from "utils/product-image";

const useProductVideoModal = (withSummaryLink?: boolean) => {
  const { courseData } = useAccess();
  const {
    lessons,
    product: { title, cover_video },
  } = courseData;
  const posterUrl = resolveThumbnailSources(courseData.product).src;
  const {
    user: { isLoggedIn },
  } = useAppContext();
  const isMobile = useIsMobile(640);

  const sources = useMemo(() => {
    let _lessons: StepPreviewCourseItem[] = [];
    if (lessons) {
      _lessons = lessons.map((courseLesson) => {
        const s: Source[] = [];
        const { hours, minutes, seconds } = formatTime(
          Number(courseLesson.duration)
        );
        const preparedTime =
          hours != "00"
            ? `${hours}:${minutes}:${seconds}`
            : `${minutes}:${seconds}`;
        if (courseLesson.is_demo) {
          if (courseLesson.online_link)
            s.push({
              label: "720p",
              url: courseLesson.online_link,
            });
          if (courseLesson.online_link_480)
            s.push({
              label: "480p",
              url: courseLesson.online_link_480,
            });
        }
        return {
          id: courseLesson.id,
          collectionTitle: courseData.product.title,
          title: courseLesson.title,
          videoSources: s,
          time: preparedTime,
          subtitleSrc: courseLesson.subtitle || "",
        };
      });
    }
    return _lessons;
  }, [lessons]);

  const loginSpan = (
    <span className="text-fds-tertiary body-md">
      نیازمند
      <Button
        variant={BUTTON_VARIANT.TEXT_ACTION}
        color={BUTTON_COLOR.PRIMARY}
        size={BUTTON_SIZE.SMALL}
        href="/my-account"
      >
        ورود/ثبت‌نام
      </Button>
    </span>
  );

  const prepareProps = (step?: StepInfo) => {
    const isFree = step ? step.is_free : true;
    const openForGuest = step ? step.open_for_guest : true;
    const { hours, minutes, seconds } = formatTime(
      Number(step?.duration || 60)
    );
    return {
      isFree,
      duration:
        hours != "00"
          ? `${hours}:${minutes}:${seconds}`
          : `${minutes}:${seconds}`,
      iconComponent: PlayIcon,
      openForGuest,
      subtitle: courseData.product.title,
      title:
        step?.online_title || step?.title || courseData.product.title || "",
      ...(Boolean(!isLoggedIn && isFree && !openForGuest) && {
        message: loginSpan,
      }),
      stepIndex: 0,
      courseType: step?.type || LESSON_STEP_TYPE.GENERAL,
      allSteps: withSummaryLink
        ? [
            {
              id: step?.id || null,
              collectionTitle: title,
              title: "چکیده آموزش",
              subtitleSrc: "",
              videoSources: [
                { label: "720", url: cover_video[720] },
                { label: "480", url: cover_video[480] },
              ] as Source[],
            },
            ...sources,
          ]
        : sources,
      id: step?.id,
    };
  };

  const showModalPreview = (step?: StepInfo) => {
    const props = prepareProps(step);
    if (isMobile) {
      modalActions.addModal(MODAL_TYPES.MOBILE_PLAYER_VIDEO, {
        data: {
          productId: courseData.product?.id,
          lessonId: step?.id || null,
          subTitle: props.subtitle,
          allSteps: props.allSteps,
          stepIndex: props.stepIndex,
          id: props.id,
          poster: posterUrl,
        },
        config: {
          closeIcon: isMobile
            ? (closeModal) => (
                <ArrowLeftIcon
                  width={28}
                  height={28}
                  className="cursor-pointer"
                  onClick={closeModal}
                />
              )
            : undefined,

          hideCloseIcon: true,
          modalClassName: "overflow-scroll",
        },
      });
    } else {
      modalActions.addModal(MODAL_TYPES.VIDEO_PLAYER, {
        data: {
          showHeader: false,
          productId: courseData.product?.id,
          lessonId: step?.id || null,
          subTitle: props.subtitle,
          allSteps: props.allSteps,
          stepIndex: props.stepIndex,
          id: props.id,
          poster: posterUrl,
        },
        config: {
          closeIcon: isMobile
            ? (closeModal) => (
                <ArrowLeftIcon
                  width={28}
                  height={28}
                  className="cursor-pointer"
                  onClick={closeModal}
                />
              )
            : undefined,
          modalClassName: "max-w-[800px] ",
          modalHeaderClassName:
            "flex justify-between items-center px-6 pt-6 pb-1.5 text-white",
          modalContentClassName:
            "h-[calc(100%-64px)] flex flex-col justify-center md:block max-h-[calc(100vh-64px)] overflow-y-auto",
          hideCloseIcon: true,
        },
      });
    }
  };

  return { prepareProps, showModalPreview };
};

export default useProductVideoModal;
